import { useEffect } from 'react';

import { create } from 'apisauce';
import { useTranslation } from 'react-i18next';

// Настройки заголовков и базового url
export const apiWrapper = create({ baseURL: '/api/v1/' });

/**
 * Получение apiWrapper с настройками заголовков, языка и базового url
 */
export const useApiWrapper = () => {
  const { i18n: { language } } = useTranslation();

  useEffect(() => {
    if (apiWrapper.headers.ACCEPT_LANGUAGE === language) {
      return;
    }

    apiWrapper.setHeader('ACCEPT-LANGUAGE', language);
  }, [language]);

  return apiWrapper;
};

export const apiUrls = {
  auth: {
    confirmationCode: 'auth/send_confirmation_code',
    signIn: '/auth/verify_confirmation_code'
  },
  locations: {
    list: '/locations',
    /**
     * Получение локаций по id города
     * @param cityId - id города
     */
    listByCity: (cityId: string) => `/locations?city_id=${cityId}`,
    create: '/locations',
    /**
     * Создать локацию
     * @param id - id
     */
    edit: (id: string) => `/locations/${id}`
  },
  guests: {
    list: '/guests',
    /**
     * Список приглашенных гостей
     * @param hallId - id зала
     */
    listByHallId: (hallId: string) => `/halls/${hallId}/guests/invited_list`,
    /**
     * Список менеджеров
     * @param hallId - id зала
     */
    importedList: (hallId: string) => `/halls/${hallId}/guests/imported_list`,
    /**
     * Редактирвать гостя
     * @param id - id гостя
     */
    edit: (id: string) => `/guests/${id}`,
    create: '/guests',
    createList: '/guests/create_list',
    downloadTemplate: '/guests/xlsx_template',
    parseXLSX: '/guests/parse_xlsx',
    /**
     * Список гостей
     * @param id - организации
     */
    guestsList: (id: string) => `organizations/${id}/guests/destroy_list`
  },
  events: {
    /**
     * Данные о мероприятии
     * @param id - мероприятия
     */
    getItem: (id: string) => `/events/${id}`,
    /**
     * Список мероприятий
     * @param page - номер страницы
     */
    getVistaList: '/films/list_for_select',
    /**
     * список мероприятий
     * @param page - текущая страница
     */
    list: (page: number) => `/events?page=${page}`,
    calendar_list: '/events/index_for_calendar',
    /**
     * Редактирование мероприятия
     * @param id - id мерпориятия
     */
    edit: (id: string) => `/events/${id}`,
    /**
     * Редактировать организаторов мероприятия
     * @param id - Мероприятие
     */
    editCustomer: (id: string) => `/events/${id}/update_customer`,
    create: '/events',
    /**
     * Опубликовать мероприятие
     * @param id - мероприятие
     */
    publish: (id: string) => `/events/${id}/publish`,
    /**
     * Архивные мероприятия
     * @param page - номер страницы
     */
    listArchive: (page: number) => `/events?page=${page}&filter=past`,
    /**
     * Скачать все места мероприятия файлом
     * @param id - id мероприятий
     */
    seatsList: (id: string) => `/events/${id}/seats_xlsx_template`
  },
  cinemas: {
    /**
     * Список кинотеатров для Vista (поиск по имени через query)
     */
    getVistaList: '/cinemas/list_for_select'
  },
  hallTemplates: {
    create: '/hall_templates',
    /**
     * Список шаблонов залов
     * @param page - страница
     */
    list: (page: number) => `/hall_templates?page=${page}`,
    /**
     * шаблон зала
     * @param id - id шаблона
     */
    show: (id: string) => `/hall_templates/${id}`,
    /**
     * Список шаблонов в локации
     * @param locationId - id локации
     */
    listWithLocationId: (locationId: string) => `/hall_templates?location_id=${locationId}&&per_page=100`
  },
  halls: {
    createList: '/halls/create_list',
    /**
     * Создать зал в мероприятии
     * @param eventId - мероприятие
     */
    listByEvent: (eventId: string) => `/events/${eventId}/halls/`,
    /**
     * Загрузить схему зала
     * @param id - id зала
     */
    show: (id: string) => `/halls/${id}`,
    updateList: '/halls/update_list',
    /**
     * Создать схему, изменив шаблон
     * @param id - id шаблона
     */
    createListByTemplates: (id: string) => `/events/${id}/halls/create_list_by_templates`
  },
  cities: {
    list: '/cities',
    listWithLocations: '/cities?with_locations=true',
    listWithHallTemplates: '/cities?with_hall_templates=true'
  },
  users: {
    list: '/users',
    /**
     * Список пользователей
     * @param page - номер страницы
     */
    pageList: (page: number) => `/users?page=${page}`,
    /**
     * Данные одного пользователя
     * @param id - id пользователя
     */
    show: (id: string) => `/users/${id}`,
    /**
     * Список мероприятий пользователя
     * @param id - id пользоваетля
     */
    eventsList: (id:string) => `/users/${id}/events`
  },
  roles: { list: '/roles' },
  sectors: {
    /**
     * Удалить сектор зала
     * @param hallId - id зала
     */
    delete: (hallId: string) => `/halls/${hallId}/destroy_sectors`,
    /**
     * Сектора зала
     * @param id - id зала
     */
    edit: (id: string) => `/sectors/${id}`,
    /**
     * Все сектора зала
     * @param hallId - id зала
     */
    listByHall: (hallId: string) => `/sectors?hall_id=${hallId}`,
    create: '/sectors'
  },
  tickets: {
    create: '/tickets',
    createList: '/tickets/create_list',
    changeGuest: 'tickets/change_guest',
    deleteList: 'tickets/destroy_list',
    /**
     * Данные билета
     * @param ticketId - id билета
     */
    edit: (ticketId: string) => `/tickets/${ticketId}`,
    /**
     * Все билеты мероприятия
     * @param eventId - id мероприятия
     */
    list: (eventId: string) => `/events/${eventId}/tickets`,
    /**
     * Данные билета мероприятия
     * @param eventId - id мероприятия
     * @param ticketId - id билета
     */
    show: (eventId: string, ticketId: string) => `/events/${eventId}/tickets${ticketId}`,
    sendEmail: '/tickets/send_email',
    /**
     * Выгрузка билетов мероприятия в XLSX
     * @param eventId - id мероприятия
     * @returns url для выгрузки
     */
    exportXlsx: (eventId: string) => `/events/${eventId}/tickets/export_xlsx`
  },
  orders: {
    create: '/orders',
    refund: '/orders/cancel_bookings'
  },
  tags: {
    /**
     * Список тегов
     * @param page - номер страницы
     */
    pageList: (page: number) => `/tags?page=${page}`,
    /** Создать тег */
    create: '/tags',
    /**
     * Редактировать тег
     * @param id - id тега
     */
    edit: (id: string) => `/tags/${id}`,
    /** Массовое удаление тегов */
    deleteList: '/tags/destroy_list'
  },
  vistaSync: {
    /** Запустить синхронизацию с Vista */
    create: '/vista_sync',
    /** Получить статус синхронизации с Vista */
    status: '/vista_sync'
  }
};
